import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'src/constants/path';
import { type RootState } from 'src/redux/store';

import NotificationBubble, { BubbleNotification } from './NotificationBubble';

const MAX_VISIBLE = 3;
const LEAVE_ANIMATION_MS = 300;

interface BubbleItem {
    instanceId: string;
    notification: BubbleNotification;
    isLeaving: boolean;
}

interface IProps {
    /** Array of notifications received from the socket. Append-only — new items are detected by comparing length against prior renders. */
    notifications: BubbleNotification[];
}

const NotificationBubbleStack = ({ notifications }: IProps) => {
    const navigate = useNavigate();
    const { appearance } = useSelector((state: RootState) => state.basic);
    const [bubbles, setBubbles] = useState<BubbleItem[]>([]);

    // Tracks how many notifications from `notifications` prop we've already processed
    const processedCountRef = useRef(0);
    // Tracks timers scheduled to remove leaving bubbles so we can cancel on unmount
    const scheduledRemovals = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const scheduleRemoval = useCallback((instanceId: string) => {
        if (scheduledRemovals.current.has(instanceId)) return;

        const timer = setTimeout(() => {
            setBubbles((prev) => prev.filter((b) => b.instanceId !== instanceId));
            scheduledRemovals.current.delete(instanceId);
        }, LEAVE_ANIMATION_MS);

        scheduledRemovals.current.set(instanceId, timer);
    }, []);

    // Dismiss by notification ID — called both by the bubble's own timer/close button and by
    // the stack when it needs to evict the oldest visible bubble to make room for a new one.
    const dismissByNotificationId = useCallback(
        (notificationId: number) => {
            setBubbles((prev) => {
                const target = prev.find((b) => b.notification.id === notificationId && !b.isLeaving);

                if (!target) return prev;

                scheduleRemoval(target.instanceId);

                return prev.map((b) => (b.instanceId === target.instanceId ? { ...b, isLeaving: true } : b));
            });
        },
        [scheduleRemoval]
    );

    // Process newly arrived notifications (append-only detection via length comparison)
    useEffect(() => {
        if (notifications.length <= processedCountRef.current) return;

        const newOnes = notifications.slice(processedCountRef.current);
        processedCountRef.current = notifications.length;

        newOnes.forEach((notification) => {
            const instanceId = `${notification.id}-${Date.now()}`;

            setBubbles((prev) => {
                const visibleCount = prev.filter((b) => !b.isLeaving).length;
                let updated = [...prev];

                // If already at the limit, evict the oldest visible bubble early
                if (visibleCount >= MAX_VISIBLE) {
                    const oldestIdx = updated.findIndex((b) => !b.isLeaving);

                    if (oldestIdx !== -1) {
                        const evicted = updated[oldestIdx];
                        updated[oldestIdx] = { ...evicted, isLeaving: true };
                        scheduleRemoval(evicted.instanceId);
                    }
                }

                return [{ instanceId, notification, isLeaving: false }, ...updated];
            });
        });
    }, [notifications, scheduleRemoval]);

    // Clear all pending removal timers on unmount
    useEffect(
        () => () => {
            scheduledRemovals.current.forEach((timer) => clearTimeout(timer));
        },
        []
    );

    const handleBubbleClick = useCallback(
        (notification: BubbleNotification) => {
            dismissByNotificationId(notification.id);

            const isMessageAction = notification.metadata?.action?.toLowerCase() === 'message';

            if (notification.moduleName === 'QUOTE' && notification.referenceId) {
                navigate(`${PATH.MY_QUOTE}/${notification.referenceId}`, {
                    state: isMessageAction ? { openMessagesTab: true } : undefined,
                });
            } else if (notification.moduleName === 'ORDER' && notification.referenceId) {
                navigate(`${PATH.ORDER_VIEW}/${notification.referenceId}`, {
                    state: isMessageAction ? { openMessagesTab: true } : undefined,
                });
            }
        },
        [dismissByNotificationId, navigate]
    );

    if (bubbles.length === 0) return null;

    return (
        <div className="fixed right-4 z-[9999] flex flex-col gap-y-2 pointer-events-none" style={{ top: '60px' }} aria-label="Live notifications">
            {bubbles.map((bubble) => (
                <div key={bubble.instanceId} className="pointer-events-auto">
                    <NotificationBubble
                        notification={bubble.notification}
                        isLeaving={bubble.isLeaving}
                        timezone={appearance?.timezone}
                        onDismiss={dismissByNotificationId}
                        onClick={handleBubbleClick}
                    />
                </div>
            ))}
        </div>
    );
};

export default NotificationBubbleStack;
